import { transient, computedFrom, Disposable } from "aurelia-framework";
import { LookupClientService } from "../../../../../../services/lookups/lookup-client.service";
import { LookupValue, LookupValueRequest } from "../../../../../../models/lookups/lookup.models";
import { LookupValueQuery } from "../../../../../../constants/modules/lookup.constants";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { IncidentStatusEnum } from "../../../../../../models/vetting/vetting.models";
import { IncidentService } from "../../../../../../services/incident/incident.service";
import { AddIncident } from "../../../incident/add-incident/add-incident";
import { EventAggregator } from "aurelia-event-aggregator";
import { Messages } from "../../../../../../configuration/event-messages.constants";
import { ValidationController, ValidationRules } from "aurelia-validation";
import { CustomValidationRenderer } from "../../../../../../client-validation-system/custom-validation-renderer";
import {UserClaimsService} from "../../../../../../services/authentication/user-claims.service";
import {Claims} from "../../../../../../constants/authentication/claims";
import { Reflection } from "../../../../../../utilities/reflection";

@transient()
export class IncidentStatusDialog {

    setIncidentStatus: any;
    awaitingResponse: number = IncidentStatusEnum.AwaitingResponse;
    misreported: number = IncidentStatusEnum.Misreported;
    incidentId: number;
    comments: string;
    statusSnippet: string;
    subtaskTypes: number[] = [];
    reload: (() => void);
    meta: any = {};
    isDropdownOpen: boolean;
    incidentStatusId: number;
    incidentStatuses: LookupValue[];
    currentIncidentStatus: LookupValue;
    misreportedReasonId: number;
    vesselName: string;
    internationalMaritimeOrganizationNumber: string;
    europeanNumberOfIdentification: string;
    vehicleIdentificationNumber: string;
    subscriptions: Disposable[] = [];
    canAssignMisreportedStatus: boolean;
    triggerCommentValidation:boolean = false;
    
    @computedFrom('currentIncidentStatus')
    get isCloseOut() {
        return this.currentIncidentStatus && this.currentIncidentStatus.id == IncidentStatusEnum.Closed;
    }

    @computedFrom('currentIncidentStatus')
    get isAwaitingResponse() {
        return this.currentIncidentStatus && this.currentIncidentStatus.id == IncidentStatusEnum.AwaitingResponse;
    }

    @computedFrom('currentIncidentStatus')
    get isMisreported() {
        return this.currentIncidentStatus && this.currentIncidentStatus.id == IncidentStatusEnum.Misreported;
    }

    @computedFrom('incidentStatusId')
    get wasClosed() {
        const statuses = [
            IncidentStatusEnum.Closed
        ];

        return statuses.indexOf(this.incidentStatusId) != -1;
    }

    addValidationRules(){
        const validationRules = ValidationRules
        .ensure('subtaskTypes')
        .required()
        .minItems(1)
        .withMessage("Reason for status change is required")
        .when(() => this.isAwaitingResponse)
        .ensure('misreportedReasonId')
        .required()
        .when(() => this.isMisreported)
        .withMessage("Reason for misreported is required");

        if(this.triggerCommentValidation){
            validationRules.ensure("comments")
            .satisfies(x=> x && x.length > 0)
            .withMessage("Please add a comment to describe the status change");
        }
        
        validationRules.on(this);
    }

    constructor(private lookupClientService: LookupClientService, private dialogClientService: DialogClientService,
        private incidentClientService: IncidentService, public addIncident: AddIncident,
        private eventAggregator: EventAggregator, private validationController: ValidationController, private userClaimsService: UserClaimsService) {

            this.validationController.addRenderer(new CustomValidationRenderer());

            this.canAssignMisreportedStatus = this.userClaimsService.hasPermission(Claims.CanAssignMisreportedStatus);
    }

    async activate(model) {
        this.reload = model.reload;
        this.incidentId = model.incidentId;
        this.incidentStatusId = model.incidentStatusId;
        this.setIncidentStatus = model.setIncidentStatus;
        this.vesselName = model.vesselName;
        this.internationalMaritimeOrganizationNumber = model.internationalMaritimeOrganizationNumber;
        this.europeanNumberOfIdentification = model.europeanNumberOfIdentification;
        this.vehicleIdentificationNumber = model.vehicleIdentificationNumber;

        const request = new LookupValueRequest();
        request.lookupValueQuery = LookupValueQuery.IncidentStatuses;
        const response = await this.lookupClientService.getLookup(request);

        this.incidentStatuses = response.activeValues;
        if(!this.canAssignMisreportedStatus){
            this.incidentStatuses = this.incidentStatuses.filter(x => x.id != IncidentStatusEnum.Misreported)
        }  
        if (this.wasClosed) {
            this.incidentStatuses = this.incidentStatuses.filter(x => x.id == IncidentStatusEnum.InProgress || x.id == this.incidentStatusId)
        }
        this.currentIncidentStatus = this.incidentStatuses.filter(x => x.id == (this.wasClosed ? IncidentStatusEnum.InProgress : this.incidentStatusId))[0];

        this.subscriptions.push(this.eventAggregator.subscribe(Messages.documentClick,  () => {
            if (this.isDropdownOpen) {
                this.closeDropdown();
            }
        }));
    }

    detached() {
        if (this.subscriptions) {
            this.subscriptions.forEach(x => x.dispose());
            this.subscriptions = [];
        }
    }

    @computedFrom(Reflection.getPropertyPath<IncidentStatusDialog>(z => z.currentIncidentStatus),
        Reflection.getPropertyPath<IncidentStatusDialog>(z => z.incidentStatusId),
        Reflection.getPropertyPath<IncidentStatusDialog>(z => z.currentIncidentStatus.id))
    get submitEnabled(): boolean {
        return this.currentIncidentStatus && this.incidentStatusId != this.currentIncidentStatus.id;
    }

    openDropdown(event) {
        event.stopPropagation();
        this.isDropdownOpen = !this.isDropdownOpen;
    }

    closeDropdown() {
        this.isDropdownOpen = false;
    }

    changeIncidentStatus(value: LookupValue) {
        if (!this.currentIncidentStatus || this.currentIncidentStatus.id != value.id)
            this.meta = {};            
        if(this.currentIncidentStatus.id != IncidentStatusEnum.Created && value.id == IncidentStatusEnum.Created)
            this.triggerCommentValidation = true;
        this.currentIncidentStatus = value;
        this.addValidationRules();
    }

    cancel() {
        this.dialogClientService.hide();
    }

    async submit() {
        this.addValidationRules();
        const validationResponse = await this.validationController.validate();

        if (!validationResponse.valid) return;

        try {
            if (this.isCloseOut)
                await this.addIncident.submit(false, false);
            const meta = JSON.stringify(this.meta);

            if (this.isCloseOut && this.addIncident && this.addIncident.item) {
                this.comments = this.addIncident.item.closeOutComments;
            }

            await this.incidentClientService.changeStatus(this.incidentId, this.currentIncidentStatus.id, this.statusSnippet, this.comments, meta, this.subtaskTypes, this.misreportedReasonId);

            if ((this.isCloseOut || this.misreportedReasonId || this.wasClosed || this.currentIncidentStatus.id == IncidentStatusEnum.AwaitingResponse) &&  this.reload)
                this.reload();
            this.dialogClientService.hide();
        } catch (e) {
            console.error("Failed to submit incident status change", e);
        }
    }

    @computedFrom(Reflection.getPropertyPath<IncidentStatusDialog>(z => z.currentIncidentStatus),
        Reflection.getPropertyPath<IncidentStatusDialog>(z => z.currentIncidentStatus.id))
    get commentsLabel() {
        if (this.currentIncidentStatus) {
            switch (this.currentIncidentStatus.id) {
                case IncidentStatusEnum.AwaitingResponse:
                    return "Status snippet";
                case IncidentStatusEnum.Closed:
                    return "Final Comments";
                case IncidentStatusEnum.Misreported:
                    return "Final Comments";
            }
        }

        return "Comments"
    }

    lastSelectedItemIds: number[] = [];

    selectedItemsChanged($event) {
        const notSelected = $event.detail.filter(x => !x.isSelected);

        if (this.statusSnippet) {
            notSelected.forEach(x => {
                const index = this.lastSelectedItemIds.indexOf(x.id);
                if (index >= 0)
                    this.lastSelectedItemIds.splice(index, 1);
                this.statusSnippet = this.statusSnippet.split(`, ${x.name}`).join("");
                this.statusSnippet = this.statusSnippet.split(`${x.name}, `).join("");
                this.statusSnippet = this.statusSnippet.split(x.name).join("");
            });
        }

        const newItems = $event.detail.filter(x => x.isSelected && this.lastSelectedItemIds.indexOf(x.id) == -1);
        newItems.forEach(x => {
            if (this.statusSnippet && !this.statusSnippet.endsWith(", "))
                this.statusSnippet += ", ";
            else if (!this.statusSnippet) {
                this.statusSnippet = "";
            }

            if (x && x.name)
                this.statusSnippet += x.name;

            this.lastSelectedItemIds.push(x.id);
        });
    }
}
